import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SEOHead from '../../components/SEO/SEOHead';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const { t } = useTranslation();

  const seoData = {
    title: 'درباره گلخانه سبز آریا - 15 سال تجربه در ساخت گلخانه‌های هوشمند | تاریخچه و تیم ما',
    description: 'آشنایی با شرکت گلخانه سبز آریا، پیشرو در صنعت گلخانه‌سازی ایران با بیش از 15 سال تجربه. تاریخچه شرکت، تیم متخصص، ماموریت و چشم‌انداز ما در توسعه کشاورزی مدرن ایران.',
    keywords: 'درباره گلخانه سبز آریا، تاریخچه شرکت آریا، تیم متخصص گلخانه، ماموریت شرکت، چشم‌انداز گلخانه‌سازی، تجربه 15 ساله، پیشرو در گلخانه‌سازی ایران، شرکت معتبر گلخانه، تخصص در گلخانه هوشمند، فناوری هلندی ایران',
    ogImage: 'https://public.readdy.ai/ai/img_res/1c8a76cd28c0c9657a3cf8e6cb82edfd.jpg',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Organization",
        "name": "گلخانه سبز آریا",
        "description": "شرکت پیشرو در ساخت گلخانه‌های هوشمند با 15 سال تجربه",
        "foundingDate": "2009",
        "numberOfEmployees": "25-50",
        "industry": "Agriculture Technology",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IR",
          "addressLocality": "Tehran"
        },
        "employee": [
          {
            "@type": "Person",
            "name": "مهندس علی احمدی",
            "jobTitle": "مدیرعامل",
            "description": "بیش از 20 سال تجربه در صنعت گلخانه‌سازی"
          },
          {
            "@type": "Person",
            "name": "مهندس سارا محمدی",
            "jobTitle": "مهندس کشاورزی",
            "description": "متخصص طراحی سیستم‌های هوشمند گلخانه"
          }
        ]
      }
    }
  };

  const stats = [
    { number: '15+', label: t('about.stats.experience'), icon: 'ri-time-line' },
    { number: '50+', label: t('about.stats.projects'), icon: 'ri-building-line' },
    { number: '150,000+', label: t('about.stats.area'), icon: 'ri-ruler-line' },
    { number: '100+', label: t('about.stats.customers'), icon: 'ri-user-smile-line' }
  ];

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: t('about.values.innovation.title'),
      description: t('about.values.innovation.description')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('about.values.quality.title'),
      description: t('about.values.quality.description')
    },
    {
      icon: 'ri-customer-service-line',
      title: t('about.values.service.title'),
      description: t('about.values.service.description')
    },
    {
      icon: 'ri-leaf-line',
      title: t('about.values.sustainability.title'),
      description: t('about.values.sustainability.description')
    }
  ];

  const team = [
    {
      name: t('about.team.ceo.name'),
      position: t('about.team.ceo.position'),
      description: t('about.team.ceo.description'),
      image: 'https://readdy.ai/api/search-image?query=Professional%20Iranian%20CEO%20in%20agricultural%20industry%2C%20middle-aged%20man%20in%20business%20suit%2C%20confident%20leadership%20pose%2C%20greenhouse%20background%2C%20executive%20portrait&width=300&height=300&seq=team-ceo&orientation=squarish'
    },
    {
      name: t('about.team.engineer.name'),
      position: t('about.team.engineer.position'),
      description: t('about.team.engineer.description'),
      image: 'https://readdy.ai/api/search-image?query=Professional%20Iranian%20agricultural%20engineer%2C%20technical%20expert%20in%20greenhouse%20technology%2C%20wearing%20engineering%20attire%2C%20modern%20greenhouse%20facility%20background&width=300&height=300&seq=team-engineer&orientation=squarish'
    },
    {
      name: t('about.team.consultant.name'),
      position: t('about.team.consultant.position'),
      description: t('about.team.consultant.description'),
      image: 'https://readdy.ai/api/search-image?query=Professional%20Iranian%20agricultural%20consultant%2C%20expert%20advisor%20in%20greenhouse%20projects%2C%20business%20casual%20attire%2C%20consulting%20with%20clients%20in%20greenhouse%20setting&width=300&height=300&seq=team-consultant&orientation=squarish'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Hero Section - طراحی حرفه‌ای جدید */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Parallax Effect */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20modern%20glass%20greenhouse%20complex%20in%20Iran%2C%20professional%20agricultural%20facility%20with%20geometric%20glass%20structures%2C%20advanced%20technology%20visible%2C%20golden%20hour%20lighting%20with%20dramatic%20sky%2C%20corporate%20headquarters%20atmosphere%2C%20impressive%20scale%20and%20modern%20architecture&width=1920&height=1080&seq=about-hero&orientation=landscape')`
          }}
        >
          {/* Multi-layer Overlay for Professional Look */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-gray-900/70 to-emerald-900/60"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/30 via-transparent to-teal-900/30"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute top-1/3 right-16 w-40 h-40 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-cyan-500/10 rounded-full blur-xl animate-pulse delay-2000"></div>
          <div className="absolute bottom-1/3 right-1/3 w-36 h-36 bg-blue-500/10 rounded-full blur-2xl animate-pulse delay-3000"></div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full px-4 lg:px-8 pt-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Content */}
              <div className="text-white space-y-8">
                {/* Badge */}
                <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-emerald-300 font-medium">{t('about.hero.badge')}</span>
                </div>

                {/* Main Title */}
                <div className="space-y-4">
                  <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                    <span className="bg-gradient-to-r from-white via-emerald-100 to-teal-100 bg-clip-text text-transparent drop-shadow-2xl">
                      {t('about.hero.title')}
                    </span>
                  </h1>
                  <div className="w-24 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full"></div>
                </div>

                {/* Subtitle */}
                <p className="text-2xl lg:text-3xl text-emerald-200 font-semibold leading-relaxed drop-shadow-lg">
                  {t('about.hero.subtitle')}
                </p>

                {/* Description */}
                <p className="text-lg lg:text-xl text-gray-200 leading-relaxed max-w-2xl drop-shadow-md">
                  {t('about.hero.description')}
                </p>

                {/* Key Features */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <i className="ri-award-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{t('about.hero.features.experience')}</h3>
                      <p className="text-emerald-200 text-sm">{t('about.hero.features.experience_desc')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                      <i className="ri-global-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{t('about.hero.features.international')}</h3>
                      <p className="text-teal-200 text-sm">{t('about.hero.features.international_desc')}</p>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    to="/contact"
                    className="group bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:-translate-y-1 flex items-center justify-center space-x-3"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    <span>{t('about.hero.cta.contact')}</span>
                    <i className="ri-arrow-left-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
                  </Link>
                  
                  <Link
                    to="/projects"
                    className="group bg-gradient-to-r from-white/20 to-white/10 backdrop-blur-sm border-2 border-white/50 text-white hover:bg-white hover:text-emerald-700 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-white/25 whitespace-nowrap cursor-pointer transform hover:scale-105 hover:-translate-y-1 flex items-center justify-center space-x-3"
                  >
                    <i className="ri-building-2-line text-xl"></i>
                    <span>{t('about.hero.cta.projects')}</span>
                    <i className="ri-external-link-line text-lg group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"></i>
                  </Link>
                </div>
              </div>

              {/* Right Content - Stats Cards */}
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="group bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 hover:bg-white/15 transition-all duration-500 hover:scale-105 hover:-translate-y-2"
                    >
                      <div className="text-center space-y-3">
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center mx-auto group-hover:shadow-lg group-hover:shadow-emerald-500/25 transition-all duration-300">
                          <i className={`${stat.icon} text-white text-2xl`}></i>
                        </div>
                        <h3 className="text-3xl lg:text-4xl font-bold text-white group-hover:text-emerald-200 transition-colors duration-300">
                          {stat.number}
                        </h3>
                        <p className="text-emerald-200 font-medium text-sm leading-relaxed">
                          {stat.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Achievement Badge */}
                <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-md border border-emerald-400/30 rounded-3xl p-6 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/25">
                    <i className="ri-trophy-line text-white text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('about.hero.achievement.title')}</h3>
                  <p className="text-emerald-200 text-sm">{t('about.hero.achievement.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-2 h-2 bg-emerald-400 rounded-full animate-ping"></div>
        <div className="absolute top-32 right-20 w-1 h-1 bg-teal-400 rounded-full animate-ping delay-1000"></div>
        <div className="absolute bottom-32 left-10 w-3 h-3 bg-cyan-400 rounded-full animate-ping delay-2000"></div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-white via-gray-50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section - Enhanced */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 bg-emerald-100 px-6 py-3 rounded-full mb-6">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-emerald-700 font-semibold">{t('about.story.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
              {t('about.story.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              {t('about.story.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="space-y-8">
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-seedling-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{t('about.story.founding.title')}</h3>
                    <p className="text-gray-700 leading-relaxed">{t('about.story.founding.description')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-rocket-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{t('about.story.growth.title')}</h3>
                    <p className="text-gray-700 leading-relaxed">{t('about.story.growth.description')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-trophy-line text-cyan-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{t('about.story.achievements.title')}</h3>
                    <p className="text-gray-700 leading-relaxed">{t('about.story.achievements.description')}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20greenhouse%20facility%20construction%20timeline%2C%20before%20and%20after%20transformation%2C%20Iranian%20agricultural%20development%2C%20professional%20corporate%20story%20visualization%2C%20Aria%20Green%20House%20company%20history%2C%20successful%20greenhouse%20projects&width=600&height=500&seq=story-main&orientation=landscape"
                alt={t('about.story.image_alt')}
                className="rounded-3xl shadow-2xl object-cover object-top w-full h-[500px]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent rounded-3xl"></div>
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6">
                <h4 className="font-bold text-slate-800 mb-2">{t('about.story.highlight.title')}</h4>
                <p className="text-gray-700 text-sm">{t('about.story.highlight.description')}</p>
              </div>
            </div>
          </div>

          {/* Timeline Section */}
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-lg">
            <h3 className="text-3xl font-bold text-center text-slate-800 mb-12">{t('about.story.timeline.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-emerald-600">2009</span>
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t('about.story.timeline.2009.title')}</h4>
                <p className="text-gray-600 text-sm">{t('about.story.timeline.2009.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-teal-600">2015</span>
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t('about.story.timeline.2015.title')}</h4>
                <p className="text-gray-600 text-sm">{t('about.story.timeline.2015.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-cyan-600">2020</span>
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t('about.story.timeline.2020.title')}</h4>
                <p className="text-gray-600 text-sm">{t('about.story.timeline.2020.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">2024</span>
                </div>
                <h4 className="font-bold text-slate-800 mb-2">{t('about.story.timeline.2024.title')}</h4>
                <p className="text-gray-600 text-sm">{t('about.story.timeline.2024.description')}</p>
              </div>
            </div>
          </div>

          {/* Company Milestones */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-3xl p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-building-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{t('about.story.milestones.projects.title')}</h3>
                  <p className="text-emerald-600 font-semibold">{t('about.story.milestones.projects.subtitle')}</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">{t('about.story.milestones.projects.description')}</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>{t('about.story.milestones.projects.item1')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>{t('about.story.milestones.projects.item2')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-emerald-600"></i>
                  <span>{t('about.story.milestones.projects.item3')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-3xl p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <i className="ri-global-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{t('about.story.milestones.expansion.title')}</h3>
                  <p className="text-teal-600 font-semibold">{t('about.story.milestones.expansion.subtitle')}</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">{t('about.story.milestones.expansion.description')}</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>{t('about.story.milestones.expansion.item1')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>{t('about.story.milestones.expansion.item2')}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-teal-600"></i>
                  <span>{t('about.story.milestones.expansion.item3')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Innovation Focus */}
          <div className="mt-16 bg-gradient-to-r from-slate-800 to-gray-800 rounded-3xl p-8 md:p-12 text-white">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">{t('about.story.innovation.title')}</h3>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">{t('about.story.innovation.subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-lightbulb-line text-white text-3xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-3">{t('about.story.innovation.research.title')}</h4>
                <p className="text-gray-300">{t('about.story.innovation.research.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-cpu-line text-white text-3xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-3">{t('about.story.innovation.technology.title')}</h4>
                <p className="text-gray-300">{t('about.story.innovation.technology.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-leaf-line text-white text-3xl"></i>
                </div>
                <h4 className="text-xl font-bold mb-3">{t('about.story.innovation.sustainability.title')}</h4>
                <p className="text-gray-300">{t('about.story.innovation.sustainability.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-target-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('about.mission.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('about.mission.description')}</p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-eye-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('about.vision.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('about.vision.description')}</p>
            </div>

            {/* Values */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-line text-cyan-600 text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('about.values.title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('about.values.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
              {t('about.values.section_title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.values.section_description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl hover:shadow-emerald-100/50 transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-emerald-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 to-gray-700 bg-clip-text text-transparent mb-6">
              {t('about.team.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.team.description')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 text-center">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover object-top mx-auto mb-6 border-4 border-emerald-100"
                  loading="lazy"
                />
                <h3 className="text-xl font-bold text-slate-800 mb-2">{member.name}</h3>
                <p className="text-emerald-600 font-semibold mb-4">{member.position}</p>
                <p className="text-gray-600 leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl mb-8 leading-relaxed">
            {t('about.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-emerald-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-2xl hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              {t('about.cta.contact')}
            </Link>
            <Link
              to="/services"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald-600 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('about.cta.services')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
